import { ChangeEvent, KeyboardEvent, useState } from 'react';

import { Icon, IconButton, TextField } from '@atom/mui';
import { EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';

import MediaLightboxDetailRow from './MediaLightboxDetailRow';
import MediaLightboxEditActions from './MediaLightboxEditActions';
import useMediaUpdate from './useMediaUpdate';

import classes from './mediaLightbox.module.css';

const MAX_ROWS = 8;

const styles = {
  whiteText: {
    color: colors.neutral.white,
  },
  editButton: {
    marginTop: '-0.3125rem',
  },
};

export type MediaLightboxDescriptionFieldProps = {
  media: Media;
  editable: boolean;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onSaved: () => void;
};

const MediaLightboxDescriptionField = ({
  media,
  editable,
  editing,
  onEdit,
  onDone,
  onSaved,
}: MediaLightboxDescriptionFieldProps) => {
  const [draft, setDraft] = useState<string>('');
  const { save, saving, saveError, reset } = useMediaUpdate(media.id);

  const handleEdit = () => {
    reset();
    setDraft(media.description || '');
    onEdit();
  };

  const handleSave = async () => {
    const description = draft.trim();

    if (description === (media.description || '')) {
      onDone();
      return;
    }

    const saved = await save({ description });

    if (saved) {
      onSaved();
      onDone();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (saving) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onDone();
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSave();
    }
  };

  const readValue = (
    <div className={classes.editableValue}>
      {editable && (
        <IconButton
          size="small"
          style={styles.editButton}
          onClick={handleEdit}
          data-cy="mediaLightboxDescriptionEditButton"
        >
          <Icon color={colors.neutral.white} icon={EditIcon} />
        </IconButton>
      )}
      <span>{media.description || ''}</span>
    </div>
  );

  const editor = (
    <div className={classes.editField} onKeyDown={handleKeyDown}>
      <TextField
        value={draft}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value)
        }
        multiline
        maxRows={MAX_ROWS}
        autoFocus
        disabled={saving}
        variant="standard"
        slotProps={{ input: { style: styles.whiteText } }}
        data-cy="mediaLightboxDescriptionInput"
      />
      {!!saveError && (
        <div
          className={classes.editError}
          data-cy="mediaLightboxDescriptionError"
        >
          {saveError}
        </div>
      )}
      <MediaLightboxEditActions
        dataCy="mediaLightboxDescription"
        saving={saving}
        saveDisabled={false}
        onCancel={onDone}
        onSave={handleSave}
      />
    </div>
  );

  return (
    <MediaLightboxDetailRow label="Description" stacked={editing}>
      {editing ? editor : readValue}
    </MediaLightboxDetailRow>
  );
};

export default MediaLightboxDescriptionField;
